import type { BatteryProposalDto } from "~/api/dto/battery-proposal.dto"
import type { ProposalConsumptionDto } from "~/api/dto/proposal-consumption.dto"

/**
 * Annual usage a Consumption Profile save left behind. A selection saved from
 * monthly figures carries no annual total, so the months stand in for it.
 */
export const savedAnnualEnergyUse = (
  consumption: ProposalConsumptionDto | undefined,
): number | null => {
  if (!consumption) {
    return null
  }

  if (consumption.annualEnergyUse != null) {
    return consumption.annualEnergyUse
  }

  if (
    Array.isArray(consumption.monthlyEnergyUse) &&
    consumption.monthlyEnergyUse.length === 12
  ) {
    const monthlyTotal = consumption.monthlyEnergyUse.reduce(
      (total, value) => total + value,
      0,
    )
    if (monthlyTotal >= 1) {
      return monthlyTotal
    }
  }

  return null
}

/**
 * Whether the Utility & consumption section is done, in the same terms the
 * gateway's completion check uses: a saved Utility Company and rate plan, and a
 * Proposal Consumption Selection. Reading the identical facts is what keeps a
 * section that looks finished from failing the final completion call with a
 * "missing a saved Utility Company" 400. Presented usage alone is not enough —
 * it can come from legacy Proposal fields with no selection behind it.
 *
 * All three are committed server state, so a seller who leaves resumes past
 * this section.
 */
export const isBatteryUtilitySaved = ({
  proposal,
  consumption,
}: {
  proposal: BatteryProposalDto | undefined
  consumption: ProposalConsumptionDto | undefined
}): boolean =>
  proposal?.tariff?.lseId != null &&
  proposal.tariff.masterTariffId != null &&
  consumption?.selection != null

/** Whether the server has recorded guided initial setup complete. */
export const isBatteryInitialSetupComplete = (
  proposal: BatteryProposalDto,
): boolean => proposal.initialSetupCompletedAt != null
